import type { IProductBundleOption } from "@rogue/universal"
import type { BundleUIOption } from "../RuleTypes"
import { applySharedRules, SharedRuleContext } from "../shared-rules"
import type { Product, IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { normalizeShelfName } from "@/js/pages/product/rackbuilder/utils/selection-utils"
import { SLINGER_ONLY_LAT_SKUS, NON_SLINGER_LAT_SKUS } from "@/js/pages/product/rackbuilder/products/cave/utils/caveConstants"

// Hides options on the rack configuration
export function getRackOptionsForTheCave(rogue: IRogue, rack: Product, bundleOption: IProductBundleOption, options: BundleUIOption[], bundleBuyRequest: BuyRequest) {
  const retval = options

  return retval.filter(opt => {
    const isPUBarOption = bundleOption.title?.toLocaleLowerCase().includes('front pull-up bar')

    // On fixed racks, hide the None PU bar option
    if (isPUBarOption && rack.name.toLowerCase().includes('fixed') && !opt.value) return false

    if (!opt.value) {
      return true
    }

    const rackDepth = rack.attributes.rack_depth
    const mainRackType = rack.name.toLowerCase().includes('fixed') ? 'fixed' : 'fold'
    const isFixedRackType = rack.name.toLowerCase().includes('fixed')
    const foldBackType = String(rack.attributes.rack_size)?.toLowerCase()?.includes('4') ? '3w' : 'full'
    const is6Post = Boolean(mainRackType === 'fixed' && rack.attributes.rack_size?.toString().includes('6'))
    const isSafetyOption = bundleOption.title?.toLocaleLowerCase().includes('safeties')
    const product = rogue.getProductById(opt.value)
    const sanitizedHardwareName = 'Matte Black'

    const context: SharedRuleContext = {
      rogue,
      rack,
      product,
      rackBeamColor: rack.attributes.beam_color,
      sanitizedHardwareName,
    }

    // On fold back racks, hide all non-None PU bar options (forces None selection)
    // Must run before applySharedRules — crown hardware rule can return true early
    if (isPUBarOption && mainRackType === 'fold') return false

    // Nameplate, PU bar, crown, beam, hardware, and 80" options
    const sharedResult = applySharedRules(context, bundleOption.title)
    if (sharedResult !== null) return sharedResult

    // We only want safeties for 6 post fixed racks and FullW Fold back racks
    if (!is6Post && isSafetyOption && isFixedRackType && foldBackType === '3w') return false

    // Hide mismatched depth options
    if (rackDepth && product.attributes.rack_depth && product.attributes.rack_depth !== rackDepth) return false

    return true
  })
}

// Hides options on left/right sides
export function getSideOptionsForTheCave(
  rogue: IRogue,
  inputCode: string,
  title: string,
  rackHeight: string | undefined,
  leftCableSelection: string | undefined,
  rightCableSelection: string | undefined,
  sideWidths: { leftWidth: string | undefined; rightWidth: string | undefined },
  options: BundleUIOption[],
  shelfSelections: Record<string, Product | undefined>,
  seatSelection: Product | undefined
) {
  const isCableSystemSelector = inputCode === 'leftcablesystem' || inputCode === 'rightcablesystem'
  const isShelf5 = title.includes('shelf 5')
  const hasShelfData = Object.values(shelfSelections).some(Boolean)

  // MARK: Cable Rules
  if (isCableSystemSelector) {
    const CTM1     = 'CTM-1'
    const Slinger = 'Slinger'
    const INDY     = 'INDY'

    if (rackHeight?.includes('80"')) {
      return options.filter(option => option.label.includes(INDY) || option.label.includes('None'))
    }

    if (rackHeight?.includes('90"')) {
      return options.filter(option => option.label.includes(CTM1) || option.label.includes(INDY) || option.label.includes(Slinger) || option.label.includes('None'))
    }

    if (rackHeight?.includes('100"')) {
      return options
    }

    // Unknown height — hide Slinger
    return options.filter(option => !option.label.includes(Slinger))
  }

  // Filter low row, trolley, and/or lat pulldown attachments based on cable selections
  const isIndySelected    = leftCableSelection?.includes('indy') || rightCableSelection?.includes('indy')
  const isCT1Selected     = leftCableSelection?.includes('ct-1') || rightCableSelection?.includes('ct-1')
  const isLP2Selected     = leftCableSelection?.includes('lp-2') || rightCableSelection?.includes('lp-2')
  const isSlingerSelected = leftCableSelection?.includes('slinger') || rightCableSelection?.includes('slinger')
  const isSlingerXSelected      = leftCableSelection?.includes('slinger x') || rightCableSelection?.includes('slinger x')
  const isLegacySlingerSelected = (leftCableSelection?.includes('slinger') && !leftCableSelection?.includes('slinger x'))
    || (rightCableSelection?.includes('slinger') && !rightCableSelection?.includes('slinger x'))
  const isNoneSelected    = !leftCableSelection && !rightCableSelection

  const isLowAttachment   = title.includes('low row')
  const isLatPullAttachment = title.includes('lat pulldown')
  const isTrolleyAttachment = title.includes('trolley')
  const isSeatAttachment    = title.includes('lat pulldown seat')

  // If no cable system is selected, hide all attachments
  if (isNoneSelected && (isLowAttachment || isLatPullAttachment || isTrolleyAttachment || isSeatAttachment)) {
    return false
  }

  // Slinger X has no seat or lat pulldown attachment options — low row is the only attachment it offers
  if (isSlingerXSelected && (isSeatAttachment || isLatPullAttachment)) {
    return false
  }

  // Filter seat options based on cable selection
  if (isSeatAttachment) {
    if (isIndySelected || isLP2Selected || isCT1Selected) return false

    return options.filter(opt => {
      if (!opt.value) return true
      if (isLegacySlingerSelected) return opt.label.toLowerCase().includes('rack mount')

      return opt.label.toLowerCase().includes('multi-use')
    })
  }

  // Hide low row and lat pulldown attachments for Indy and CT1
  if ((isIndySelected || isCT1Selected) && (isLowAttachment || isLatPullAttachment)) {
    return false
  }

  // Legacy Slinger has no swivel pulley — low row is only compatible once the seat is also selected
  if (isLegacySlingerSelected && isLowAttachment && !seatSelection) {
    return false
  }

  // Filter lat pulldown attachment options by cable system compatibility (LP-2, CTM-1, or other non-Slinger/non-Indy/non-CT1 cable selected)
  if (isLatPullAttachment) {
    if (isLegacySlingerSelected) {
      return options.filter(opt => {
        if (!opt.value) return true
        const sku = rogue.getProductById(opt.value)?.sku

        return sku === undefined || !NON_SLINGER_LAT_SKUS.includes(sku)
      })
    }

    return options.filter(opt => {
      if (!opt.value) return true
      const sku = rogue.getProductById(opt.value)?.sku

      return sku === undefined || !SLINGER_ONLY_LAT_SKUS.includes(sku)
    })
  }

  // Hide trolley attachment for LP2 and Slinger
  if ((isLP2Selected || isSlingerSelected) && isTrolleyAttachment) {
    return false
  }
   
  // Hide plate storage when the cable system is not Slinger
  if (title.includes('plate storage') && !isSlingerSelected) {
    return false
  }

  // MARK: Shelf Rules
  const rightSideWidth = sideWidths.rightWidth
  const leftSideWidth = sideWidths.leftWidth
  const sideWidth = rightSideWidth || leftSideWidth

  // Hide shelves that aren't the same width as the left/right side
  const isShelfSelector = title.toLowerCase().includes('shelf')
  if (sideWidth && isShelfSelector) {
    return options.filter(opt => {
      if (opt.label === 'None') return true
      
      return opt.label.includes(String(sideWidth))
    })
  }

  // Filter Shelf 5 Options
  if (isShelf5 && rackHeight?.includes('80"') && hasShelfData) {
    const priorShelves = [
      normalizeShelfName(shelfSelections.shelf1?.name),
      normalizeShelfName(shelfSelections.shelf2?.name),
      normalizeShelfName(shelfSelections.shelf3?.name),
      normalizeShelfName(shelfSelections.shelf4?.name),
    ]

    const hasStoragePanel = priorShelves.every(s => s?.includes('panel'))
    const hasEmptySlot = priorShelves.every(s => s === undefined)

    if (!hasStoragePanel && !hasEmptySlot) {
      const oversizedShelfCount = priorShelves.filter(s => s === 'bumper' || s === 'kettlebell shelf').length
      const largeShelfCount = priorShelves.filter(s => s === 'medball shelf' || s === 'bumper' || s === 'liner' || s === 'kettlebell shelf').length

      if (oversizedShelfCount >= 3) {
        return false
      }
        
      if (largeShelfCount >= 2) {
        return options.filter(opt => {
          const label = normalizeShelfName(opt.label)
          
          return opt.label === 'None' || label === 'panel'
        })
      }
    }
  }

  // Mini feet are hidden in the template layer and not the dynamic layer
  // so it does not break the dynamic selection functionality

  return undefined
}